/**
 * Unified API Client for Yoga Healers Admin Dashboard
 * Connects directly to the same backend as the frontend client.
 * Supports silent token refresh to prevent forced logout every 15 minutes.
 */

const getApiBase = () => {
  // When running in the browser
  if (typeof window !== 'undefined') {
    // 1. In local development, always connect to local backend port 5000
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:5000';
    }
    // 2. If running directly on backend domain (yogahealers.org/dashboard), use relative ''
    if (window.location.hostname === 'yogahealers.org' || window.location.hostname === 'www.yogahealers.org') {
      return '';
    }
    // 3. For admin subdomain (admin.yogahealers.org) or any other domain, backend API is on yogahealers.org
    return 'https://yogahealers.org';
  }
  let envUrl = process.env.REACT_APP_API_URL || 'https://yogahealers.org';
  if (envUrl.endsWith('/')) {
    envUrl = envUrl.slice(0, -1);
  }
  if (envUrl.endsWith('/api')) {
    envUrl = envUrl.slice(0, -4);
  }
  return envUrl;
};

export const API_BASE = getApiBase();

export const getApiUrl = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE}${cleanEndpoint}`;
};

// Track whether a token refresh is in progress to avoid parallel refresh calls
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, newToken = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(newToken);
  });
  refreshQueue = [];
};

/**
 * Attempt to silently refresh the admin access token using the stored refresh token.
 * Returns the new access token on success, or throws on failure.
 */
const refreshAdminToken = async () => {
  const adminRefreshToken = localStorage.getItem('adminRefreshToken');
  if (!adminRefreshToken) {
    throw new Error('No refresh token available');
  }

  const refreshUrl = getApiUrl('/api/auth/refresh-token');
  const res = await fetch(refreshUrl, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: adminRefreshToken }),
  });

  const data = await res.json();

  if (!res.ok || !data.success || !data.token) {
    throw new Error(data.message || 'Token refresh failed');
  }

  // Store the new access token
  localStorage.setItem('adminToken', data.token);
  if (data.refreshToken) {
    localStorage.setItem('adminRefreshToken', data.refreshToken);
  }

  return data.token;
};

export const apiFetch = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  const isAuthEndpoint =
    url.includes('/api/auth/admin/login') ||
    url.includes('/api/auth/refresh-token') ||
    url.includes('/api/auth/admin/logout');

  const buildHeaders = (token) => {
    const h = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    };
    if (options.body instanceof FormData) delete h['Content-Type'];
    return h;
  };

  // First attempt
  let token = localStorage.getItem('adminToken');
  let res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: buildHeaders(token),
  });

  // If 403 forbidden with deactivation, expire session immediately
  if (res.status === 403) {
    try {
      const clone = res.clone();
      const body = await clone.json();
      if (body.isDeactivated) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminRefreshToken');
        localStorage.removeItem('adminUser');
        window.dispatchEvent(new CustomEvent('admin_session_expired'));
      }
    } catch (e) {}
    return res;
  }

  // If not 401 or it's an auth endpoint, return immediately
  if (res.status !== 401 || isAuthEndpoint) {
    return res;
  }

  // --- Silent token refresh ---
  if (isRefreshing) {
    // Queue this request until the current refresh completes
    return new Promise((resolve, reject) => {
      refreshQueue.push({
        resolve: async (newToken) => {
          resolve(
            fetch(url, {
              credentials: 'include',
              ...options,
              headers: buildHeaders(newToken),
            })
          );
        },
        reject,
      });
    });
  }

  isRefreshing = true;

  try {
    const newToken = await refreshAdminToken();
    processQueue(null, newToken);

    // Retry the original request with the refreshed token
    res = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: buildHeaders(newToken),
    });

    return res;
  } catch (refreshError) {
    processQueue(refreshError, null);
    // Refresh failed — clear all credentials and force logout
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('token');
    window.dispatchEvent(new CustomEvent('admin_session_expired'));
    // Return the original 401 response so callers can handle it
    return res;
  } finally {
    isRefreshing = false;
  }
};

export default apiFetch;
